import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../../redux/store";
import {
    ComposedChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
    Scatter,
    Cell,
    ZAxis,
} from "recharts";
import {
    fetchBudgetsTecniche,
    BudgetsTecnicheList,
    resetBudgets,
} from "./budgetsSlice";
import {
    FiltriBudgets,
    setFiltriBudgets,
} from "components/userPreference/userPreferenceSlice";
import { ErrorStatusTypes } from "components/common/errorsDeclarations";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import { theme } from "components/theme";
import { Stagione, Tecnica, Macchina } from "components/anagrafiche/anagraficheSlice";
import { convertToDateTime } from "../../../common/dateUtils/convertToDateTime";

// ============================================
// HELPER - Formattazione valuta
// ============================================

const formatCurrency = (value: number | null | undefined): string => {
    return "€ " + Intl.NumberFormat("it-IT", {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value || 0);
};

const formatCompact = (value: number): string => {
    return new Intl.NumberFormat("it-IT", {
        notation: "compact",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(value);
};

// ============================================
// CUSTOM COMPONENT - Soglie Capacità
// ============================================

interface ChartDataItem {
    nome: string;
    valoreBudget: number;
    valoreConfermato: number;
    capacitaEsclusiva: number;
    capacitaCondivisa: number;
    valoreProdotto: number;
}

// ============================================
// CUSTOM SHAPES PER SOGLIE
// ============================================

interface SogliaShapeProps {
    cx?: number;
    cy?: number;
    payload?: ChartDataItem;
}

// Shape per la linea orizzontale della soglia esclusiva
const SogliaEsclusivaShape: React.FC<SogliaShapeProps> = ({ cx, cy }) => {
    if (cx === undefined || cy === undefined) return null;
    const lineWidth = 50;

    return (
        <line
            x1={cx - lineWidth / 2}
            y1={cy}
            x2={cx + lineWidth / 2}
            y2={cy}
            stroke={theme.palette.info.main}
            strokeWidth={3}
            strokeLinecap="round"
        />
    );
};

// Shape per la linea orizzontale della soglia totale
const SogliaTotaleShape: React.FC<SogliaShapeProps> = ({ cx, cy }) => {
    if (cx === undefined || cy === undefined) return null;
    const lineWidth = 50;

    return (
        <line
            x1={cx - lineWidth / 2}
            y1={cy}
            x2={cx + lineWidth / 2}
            y2={cy}
            stroke={theme.palette.warning.dark}
            strokeWidth={3}
            strokeLinecap="round"
        />
    );
};

// Shape per la linea verticale tratteggiata che collega le due soglie
interface LineaVerticaleShapeProps {
    cx?: number;
    payload?: ChartDataItem;
    yAxis?: { scale: (value: number) => number };
}

const LineaVerticaleShape: React.FC<LineaVerticaleShapeProps> = ({ cx, payload, yAxis }) => {
    if (cx === undefined || !payload || !yAxis?.scale) return null;

    const yEsclusiva = yAxis.scale(payload.capacitaEsclusiva);
    const yTotale = yAxis.scale(payload.capacitaEsclusiva + payload.capacitaCondivisa);

    if (payload.capacitaEsclusiva === 0 && payload.capacitaCondivisa === 0) return null;

    return (
        <line
            x1={cx}
            y1={yEsclusiva}
            x2={cx}
            y2={yTotale}
            stroke={theme.palette.error.main}
            strokeWidth={2}
            strokeDasharray="4 3"
        />
    );
};

// ============================================
// CUSTOM TOOLTIP
// ============================================

interface CustomTooltipProps {
    active?: boolean;
    payload?: Array<{
        name: string;
        value: number;
        color: string;
        dataKey: string;
        payload?: ChartDataItem;
    }>;
    label?: string;
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload, label }) => {
    if (!active || !payload || payload.length === 0) return null;

    // Recupera i dati originali per mostrare anche le soglie
    const dataItem = payload[0]?.payload;

    return (
        <Box sx={{
            backgroundColor: 'white',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 1,
            p: 1.5,
            boxShadow: 2,
        }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                {label}
            </Typography>
            {payload
                .filter(entry =>
                    !['capacitaEsclusiva', 'capacitaTotale', 'nome'].includes(entry.dataKey)
                ).map((entry, index) => (

                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Box sx={{
                        width: 12,
                        height: 12,
                        backgroundColor: entry.color,
                        borderRadius: '2px',
                    }} />
                    <Typography variant="body2">
                        {entry.name}: {formatCurrency(entry.value)}
                    </Typography>
                </Box>
            ))}
            {/* Soglie capacità */}
            {dataItem && (dataItem.capacitaEsclusiva > 0 || dataItem.capacitaCondivisa > 0) && (
                <>
                    <Box sx={{ borderTop: `1px solid ${theme.palette.divider}`, mt: 1, pt: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Box sx={{
                                width: 12,
                                height: 3,
                                backgroundColor: theme.palette.info.main,
                                borderRadius: '1px',
                            }} />
                            <Typography variant="body2">
                                Cap. Esclusiva: {formatCurrency(dataItem.capacitaEsclusiva)}
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{
                                width: 12,
                                height: 3,
                                backgroundColor: theme.palette.warning.dark,
                                borderRadius: '1px',
                            }} />
                            <Typography variant="body2">
                                Cap. Totale: {formatCurrency(dataItem.capacitaEsclusiva + dataItem.capacitaCondivisa)}
                            </Typography>
                        </Box>
                    </Box>
                </>
            )}
        </Box>
    );
};

// ============================================
// CUSTOM LEGEND
// ============================================

const CustomLegend: React.FC = () => {
    const items = [
        { label: 'Budget', color: theme.palette.warning.light, type: 'square' },
        { label: 'Confermato', color: theme.palette.success.main, type: 'square' },
        { label: 'Prodotto', color: theme.palette.primary.main, type: 'square' },
        { label: 'Cap. Esclusiva', color: theme.palette.info.main, type: 'line' },
        { label: 'Cap. Totale', color: theme.palette.warning.dark, type: 'line' },
    ];

    return (
        <Box sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 2,
            mb: 1,
        }}>
            {items.map((item, index) => (
                <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {item.type === 'square' ? (
                        <Box sx={{
                            width: 12,
                            height: 12,
                            backgroundColor: item.color,
                            borderRadius: '2px',
                        }} />
                    ) : (
                        <Box sx={{
                            width: 16,
                            height: 3,
                            backgroundColor: item.color,
                            borderRadius: '1px',
                        }} />
                    )}
                    <Typography variant="caption" sx={{ fontSize: 11 }}>
                        {item.label}
                    </Typography>
                </Box>
            ))}
        </Box>
    );
};

// ============================================
// COMPONENT
// ============================================

const BudgetTecnicaBarChart = () => {
    const dispatch = useAppDispatch();

    // ============================================
    // SELECTORS
    // ============================================

    const budgetsTecniche = useSelector<RootState, BudgetsTecnicheList>(
        (state) => state.budgetsState.budgetsTecnicheList
    );

    const filtri = useSelector<RootState, FiltriBudgets>(
        (state) => state.userPreferenceState.filtriBudgets || {}
    );

    const stagioni = useSelector<RootState, Stagione[]>(
        (state) => state.anagraficheState?.stagioni?.results || []
    );

    const tecniche = useSelector<RootState, Tecnica[]>(
        (state) => state.anagraficheState?.tecniche?.results || []
    );

    const macchine = useSelector<RootState, Macchina[]>(
        (state) => state.anagraficheState?.macchine?.results || []
    );

    // ============================================
    // STATI LOCALI
    // ============================================

    const [stagioneSelezionata, setStagioneSelezionata] = useState<number | undefined>(
        filtri?.stagione
    );
    const [tecnicheSelezionate, setTecnicheSelezionate] = useState<number[]>(
        filtri.tecnicheSelezionate || []
    );

    // ============================================
    // EFFECTS
    // ============================================

    useEffect(() => {
        if (stagioneSelezionata) {
            const nuoviFiltri: FiltriBudgets = {
                ...filtri,
                tecnicheSelezionate: tecnicheSelezionate.length > 0 ? tecnicheSelezionate : undefined,
                stagione: stagioneSelezionata,
            };
            dispatch(setFiltriBudgets(nuoviFiltri));
            dispatch(resetBudgets());
            dispatch(fetchBudgetsTecniche({
                stagione_id: stagioneSelezionata,
                tecniche: tecnicheSelezionate
            }));
        }
    }, [stagioneSelezionata, tecnicheSelezionate, dispatch]);

    // ============================================
    // PREPARAZIONE DATI PER IL GRAFICO
    // ============================================

    const prepareChartData = (): ChartDataItem[] => {
        const budgets = budgetsTecniche.budgetsTecniche || [];

        // Conta quante macchine sono associate a una tecnica (relazione inversa)
        const contaMacchinePerTecnica = (tecnicaId: number): number => {
            return macchine.filter(m => m.tecniche?.includes(tecnicaId)).length;
        };

        return budgets
            .map(b => ({
                nome: b.tecnica_display || `Tecnica ${b.tecnica}`,
                valoreBudget: b.valore_budget || 0,
                valoreConfermato: b.valore_confermato || 0,
                capacitaEsclusiva: b.capacita_esclusiva || 0,
                capacitaCondivisa: b.capacita_condivisa || 0,
                valoreProdotto: b.valore_prodotto || 0,
                _numMacchine: contaMacchinePerTecnica(b.tecnica),
            }))
            .sort((a, b) => a._numMacchine - b._numMacchine);
    };

    const chartData = prepareChartData();

    // ============================================
    // HANDLERS
    // ============================================

    const handleStagioneChange = (_: React.SyntheticEvent, newValue: Stagione | null) => {
        setStagioneSelezionata(newValue?.id);
    };

    const isLoading = budgetsTecniche.errorsStack.status === ErrorStatusTypes.PENDING;
    const hasData = chartData.length > 0;

    // ============================================
    // RENDER
    // ============================================

    return (
        <Card raised sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column'
        }}>
            <CardContent sx={{
                flexGrow: 1,
                display: 'flex',
                flexDirection: 'column',
                p: { xs: 2, sm: 3 }
            }}>
                {/* Header con filtro stagione */}
                <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 'medium' }}>
                            Budget per Tecnica
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Visualizzazione budget, capacità e prodotto per tecnica rispetto alla capacità produttiva alla data
                        </Typography>
                        <Typography variant="h6" color="text.secondary">
                            {convertToDateTime(new Date())?.toFormat('DD')}
                        </Typography>
                    </Grid>
                    <Grid size={{ xs: 6, md: 4 }}>
                        <Autocomplete
                            size="small"
                            value={stagioni.find(s => s.id === stagioneSelezionata) || null}
                            options={stagioni}
                            getOptionLabel={(option) => option.descrizione || ""}
                            onChange={handleStagioneChange}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    sx={{
                                        backgroundColor: stagioneSelezionata
                                            ? theme.palette.action.selected
                                            : 'inherit',
                                        borderRadius: 1
                                    }}
                                    label="Stagione"
                                    variant="outlined"
                                />
                            )}
                        />
                    </Grid>
                    <Grid size={{ xs: 6, md: 4 }}>
                        <Autocomplete
                            multiple
                            size="small"
                            options={tecniche}
                            getOptionLabel={(option) => option.nome || ""}
                            value={tecniche.filter((t) =>
                                tecnicheSelezionate.includes(t.id)
                            )}
                            onChange={(_, newValue) => {
                                setTecnicheSelezionate(newValue.map((t) => t.id));
                            }}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Filtra per Tecniche"
                                    placeholder="Tutte"
                                />
                            )}
                        />
                    </Grid>
                </Grid>

                {/* Contenuto del grafico */}
                <Box sx={{
                    flexGrow: 1,
                    position: 'relative',
                    minHeight: { xs: 300, sm: 400, lg:800 },
                    width: '100%'
                }}>
                    {isLoading && (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.8)',
                            zIndex: 1,
                            borderRadius: 1
                        }}>
                            <CircularProgress />
                        </Box>
                    )}

                    {!stagioneSelezionata && !isLoading && (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%',
                            flexDirection: 'column',
                            gap: 2
                        }}>
                            <Typography variant="h6" color="text.secondary" align="center">
                                Seleziona una stagione
                            </Typography>
                            <Typography variant="body2" color="text.secondary" align="center">
                                per visualizzare i dati del budget per tecnica
                            </Typography>
                        </Box>
                    )}

                    {stagioneSelezionata && !isLoading && !hasData && (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%'
                        }}>
                            <Typography variant="body1" color="text.secondary">
                                Nessun dato disponibile per la stagione selezionata
                            </Typography>
                        </Box>
                    )}

                    {stagioneSelezionata && !isLoading && hasData && (
                        <Box sx={{ width: '100%', height: '100%' }}>
                            {/* Legenda custom */}
                            <CustomLegend />

                            <ResponsiveContainer width="100%" height="90%">
                                <ComposedChart
                                    data={chartData}
                                    margin={{
                                        top: 20,
                                        right: 20,
                                        bottom: chartData.length > 4 ? 80 : 40,
                                        left: 60,
                                    }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                                    <XAxis
                                        dataKey="nome"
                                        tick={{
                                            fontSize: 11,
                                            fill: theme.palette.text.secondary,
                                        }}
                                        angle={chartData.length > 4 ? -45 : 0}
                                        textAnchor={chartData.length > 4 ? "end" : "middle"}
                                        height={chartData.length > 4 ? 80 : 40}
                                    />
                                    <YAxis
                                        yAxisId="left"
                                        tickFormatter={(value) => formatCompact(value)}
                                        tick={{
                                            fontSize: 11,
                                            fill: theme.palette.text.secondary,
                                        }}
                                        label={{
                                            value: 'Valore (€)',
                                            angle: -90,
                                            position: 'insideLeft',
                                            style: {
                                                textAnchor: 'middle',
                                                fill: theme.palette.text.secondary,
                                                fontSize: 12,
                                            }
                                        }}
                                    />
                                    {/* ZAxis nascosto per Scatter (serve per evitare warning) */}
                                    <ZAxis range={[0, 0]} />
                                    <Tooltip content={<CustomTooltip />} />

                                    {/* Barre impilate - Budget */}
                                    <Bar
                                        yAxisId="left"
                                        dataKey="valoreBudget"
                                        name="Budget"
                                        stackId="budget"
                                        fill={theme.palette.warning.light}
                                        radius={[0, 0, 0, 0]}
                                    />
                                    <Bar
                                        yAxisId="left"
                                        dataKey="valoreConfermato"
                                        name="Confermato"
                                        stackId="budget"
                                        fill={theme.palette.success.main}
                                        radius={[4, 4, 0, 0]}
                                    />

                                    {/* Barra singola - Prodotto */}
                                    <Bar
                                        yAxisId="left"
                                        dataKey="valoreProdotto"
                                        name="Prodotto"
                                        fill={theme.palette.primary.main}
                                        radius={[4, 4, 0, 0]}
                                    />

                                    {/* Scatter per soglia Capacità Esclusiva */}
                                    <Scatter
                                        yAxisId="left"
                                        dataKey="capacitaEsclusiva"
                                        name="Cap. Esclusiva"
                                        shape={<SogliaEsclusivaShape />}
                                        legendType="none"
                                    />

                                    {/* Scatter per soglia Capacità Totale (Esclusiva + Condivisa) */}
                                    <Scatter
                                        yAxisId="left"
                                        data={chartData.map(item => ({
                                            ...item,
                                            capacitaTotale: item.capacitaEsclusiva + item.capacitaCondivisa
                                        }))}
                                        dataKey="capacitaTotale"
                                        name="Cap. Totale"
                                        shape={<SogliaTotaleShape />}
                                        legendType="none"
                                    />
                                </ComposedChart>
                            </ResponsiveContainer>
                        </Box>
                    )}
                </Box>
            </CardContent>
        </Card>
    );
};

export default BudgetTecnicaBarChart;